/**
 * Custom Search Enhancement
 * Enables searching products by SKU and Style Code metafield
 * 
 * NOTE: For stores with 1000+ products, enable "Storefront search" on your
 * style_code metafield in Shopify Admin → Settings → Custom data → Products
 * This allows Shopify's native search to find products by style code.
 */

(function() {
  // Store the original fetch function
  const originalFetch = window.fetch;

  // Cache for all products data (from embedded JSON)
  let allProductsData = null;

  /**
   * Load and cache products data from embedded JSON
   * This contains products that have style codes (limited by Liquid to ~250)
   */
  function getAllProductsData(forceReload = false) {
    // If we already have data and not forcing reload, return it
    if (!forceReload && allProductsData !== null && allProductsData.length > 0) {
      return allProductsData;
    }

    // Try to load the data (retry if previously failed)
    try {
      const dataScript = document.getElementById('all-products-data');
      console.log('Custom Search: Looking for all-products-data script:', !!dataScript);
      
      if (dataScript) {
        const rawText = dataScript.textContent.trim();
        console.log('Custom Search: Script content length:', rawText.length);
        
        if (rawText.length > 0) {
          try {
            allProductsData = JSON.parse(rawText);
            console.log('Custom Search: Loaded', allProductsData.length, 'products with style codes');
            console.log('Custom Search: For full coverage, enable "Storefront search" on your style_code metafield in Shopify Admin');
            
            // Debug: Show first 10 style codes loaded with their types
            if (allProductsData.length > 0) {
              const sampleCodes = allProductsData.slice(0, 10).map(p => ({
                style_code: p.style_code,
                type: typeof p.style_code,
                value: String(p.style_code),
                discount_percentage: p.discount_percentage,
                has_discount: p.discount_percentage !== undefined
              }));
              console.log('Custom Search: Sample style codes loaded:', sampleCodes);
              
              // Check for products with discount_percentage
              const productsWithDiscount = allProductsData.filter(p => p.discount_percentage !== undefined && p.discount_percentage > 0);
              console.log('Custom Search: Products with discount_percentage:', productsWithDiscount.length);
              if (productsWithDiscount.length > 0) {
                console.log('Custom Search: Sample products with discount:', productsWithDiscount.slice(0, 3).map(p => ({
                  title: p.title,
                  discount: p.discount_percentage,
                  price: p.price,
                  compare_at_price: p.compare_at_price
                })));
              }
            }
            
            // Verify all products have style_code
            const productsWithoutStyleCode = allProductsData.filter(p => !p.style_code);
            if (productsWithoutStyleCode.length > 0) {
              console.warn('Custom Search: Found', productsWithoutStyleCode.length, 'products without style_code');
            }
            
            return allProductsData;
          } catch (parseError) {
            console.error('Custom Search: JSON parse error:', parseError);
            console.error('Custom Search: Raw text preview:', rawText.substring(0, 200));
            allProductsData = [];
          }
        } else {
          console.warn('Custom Search: Script element found but content is empty');
          allProductsData = [];
        }
      } else {
        console.warn('Custom Search: Script element with id "all-products-data" not found in DOM');
        // List all script elements to help debug
        const allScripts = document.querySelectorAll('script[id]');
        const scriptIds = Array.from(allScripts).map(s => s.id).filter(Boolean);
        console.log('Custom Search: Available script IDs:', scriptIds);
        console.log('Custom Search: Will retry loading when script becomes available');
        // Don't cache empty array yet - script might load later
        return [];
      }
    } catch (e) {
      console.error('Custom Search: Error loading products data', e);
      console.error('Custom Search: Error details:', e.message, e.stack);
      allProductsData = [];
    }

    return allProductsData || [];
  }

  /**
   * Search products by style_code (client-side from embedded data)
   */
  function searchByStyleCode(searchTerm) {
    if (!searchTerm || searchTerm.length < 2) {
      return { results: [], count: 0 };
    }

    const products = getAllProductsData();
    const searchLower = searchTerm.toLowerCase().trim();
    
    console.log('Custom Search: Searching for:', searchTerm, '(normalized:', searchLower, ')');
    console.log('Custom Search: Total products to search:', products.length);
    
    // Debug: Show a few sample products with their style codes
    if (products.length > 0) {
      console.log('Custom Search: Sample products:', products.slice(0, 5).map(p => ({
        title: p.title,
        style_code: p.style_code,
        style_code_type: typeof p.style_code,
        style_code_string: String(p.style_code)
      })));
    }
    
    // Filter products that match the style code
    const matches = products.filter(product => {
      if (!product.style_code) {
        return false;
      }
      
      // Normalize both search term and style code for comparison
      // Convert to string first to handle numbers
      const productStyleCode = String(product.style_code).toLowerCase().trim();
      const searchTermNormalized = searchLower;
      
      // Check for exact match first, then partial match
      const exactMatch = productStyleCode === searchTermNormalized;
      const partialMatch = productStyleCode.includes(searchTermNormalized) || searchTermNormalized.includes(productStyleCode);
      const isMatch = exactMatch || partialMatch;
      
      if (isMatch) {
        console.log('Custom Search: Match found - Product:', product.title, 'Style Code:', product.style_code, 'Type:', typeof product.style_code);
      }
      
      return isMatch;
    });

    console.log('Custom Search: Total matches found:', matches.length);

    // Sort by exact match first, then partial matches
    matches.sort((a, b) => {
      const aStyleCode = String(a.style_code).toLowerCase().trim();
      const bStyleCode = String(b.style_code).toLowerCase().trim();
      const aExact = aStyleCode === searchLower;
      const bExact = bStyleCode === searchLower;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      return 0;
    });

    if (matches.length > 0) {
      console.log('Custom Search: Found', matches.length, 'style code matches for:', searchTerm);
      console.log('Custom Search: Matched products:', matches.map(m => ({ title: m.title, style_code: m.style_code })));
    } else {
      console.warn('Custom Search: No matches found. Search term:', searchTerm);
      console.warn('Custom Search: Sample style codes in data:', products.slice(0, 5).map(p => p.style_code));
    }

    return { results: matches.slice(0, 10), count: matches.length };
  }

  /**
   * Render style code results into the dedicated style code container
   * Uses product handles to render via Shopify's product-item snippet
   */
  function renderStyleCodeResults(products) {
    // Use the dedicated style code container (won't be overwritten by predictive search)
    const styleCodeContainer = document.querySelector('[data-style-code-container]');
    
    console.log('Custom Search: renderStyleCodeResults called with', products?.length || 0, 'products');
    console.log('Custom Search: Container found:', !!styleCodeContainer);
    
    if (!products || products.length === 0) {
      console.log('Custom Search: No products to render');
      if (styleCodeContainer) {
        styleCodeContainer.innerHTML = '';
      }
      return;
    }
    
    if (!styleCodeContainer) {
      console.warn('Custom Search: Style code container not found!');
      return;
    }

    // Create product cards HTML - simplified version that links to products
    let html = '<div data-style-code-results class="quick-search__style-code-section">';
    html += '<div class="quick-search__header ff-heading fs-heading-3-base">Style Code Matches</div>';
    html += '<div class="quick-search__no-results-products grid product-grid">';
    
    products.forEach(product => {
      // Debug: Log discount_percentage if it exists
      if (product.discount_percentage !== undefined) {
        console.log('Custom Search: Product', product.title, 'has discount_percentage:', product.discount_percentage, 'type:', typeof product.discount_percentage);
      }
      
      const priceHtml = formatPrice(product);
      const hasMultipleImages = product.image_two && product.image_two !== product.image;
      
      // First image
      const imageOneHtml = product.image 
        ? `<div class="image"><img src="${product.image}" alt="${product.image_alt || product.title}" loading="lazy" class="image__img lazyload" data-src="${product.image}" style="width: 100%; height: 100%; object-fit: cover;"></div>`
        : '';
      
      // Second image (if available)
      const imageTwoHtml = hasMultipleImages && product.image_two
        ? `<div class="product-item__image product-item__image--two"><div class="image"><img src="${product.image_two}" alt="${product.image_two_alt || product.title}" loading="lazy" class="image__img lazyload" data-src="${product.image_two}" style="width: 100%; height: 100%; object-fit: cover;"></div></div>`
        : '';
      
      const mediaClass = hasMultipleImages ? ' product-item__media--multiple-images' : '';
      
      html += `
        <div class="product-item style-code-product-item${hasMultipleImages ? ' product-item--has-multiple-images' : ''}">
          <div class="product-item__inner">
            <div class="product-item__media${mediaClass}">
              <div style="display: block;" class="gw-add-to-wishlist-product-card-placeholder" 
                  data-gw-product-id="${product.id}"
                  data-gw-variant-id="${product.variant_id || product.id}"></div>
              <a href="${product.url}" class="product-item__image-link">
                ${hasMultipleImages ? `<div class="product-item__image product-item__image--one">${imageOneHtml}</div>${imageTwoHtml}` : `<div class="product-item__image">${imageOneHtml}</div>`}
              </a>
            </div>
            <div class="product-item__meta">
              <div class="product-item__text ff-body fs-body-base ta-c">
                <h4 class="product-item__product-title fs-product-title fs-body-bold">
                  <a href="${product.url}">${escapeHtml(product.title)}</a>
                </h4>
                <span class="product-item__price-wrapper">
                  ${priceHtml}
                </span>
                <div class="product-item__style-code fs-body-small" style="opacity: 0.7; margin-top: 4px;">
                  Style: ${escapeHtml(product.style_code)}
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    });
    
    html += '</div></div>';

    // Set the content of our dedicated container
    styleCodeContainer.innerHTML = html;
    
    // Ensure container and its parent are visible
    styleCodeContainer.style.display = 'block';
    styleCodeContainer.style.visibility = 'visible';
    styleCodeContainer.style.opacity = '1';
    
    // Make sure parent wrapper is visible
    const resultsWrapper = styleCodeContainer.closest('.quick-search__results-wrapper');
    if (resultsWrapper) {
      resultsWrapper.style.display = 'block';
    }
    
    // Hide other views to show style code results
    const noResultsView = document.querySelector('[data-quick-search-no-results]');
    const defaultView = document.querySelector('[data-quick-search-default]');
    const resultsContainer = document.querySelector('[data-results]');
    
    if (noResultsView) noResultsView.style.display = 'none';
    if (defaultView) defaultView.style.display = 'none';
    // Hide regular results if they're empty, but keep style code visible
    
    // Force a reflow to ensure visibility
    void styleCodeContainer.offsetHeight;
    
    console.log('Custom Search: Style code results rendered and container made visible');
    console.log('Custom Search: Container HTML length:', styleCodeContainer.innerHTML.length);
    console.log('Custom Search: Container display:', window.getComputedStyle(styleCodeContainer).display);
    console.log('Custom Search: Container visibility:', window.getComputedStyle(styleCodeContainer).visibility);
    
    // Initialize product item animations for hover effects
    // Try multiple ways to access the ProductItem function
    setTimeout(() => {
      const productItemsContainer = styleCodeContainer.querySelector('.quick-search__no-results-products');
      if (!productItemsContainer) return;
      
      // Try accessing through theme object
      if (window.theme && typeof window.theme.ProductItem === 'function') {
        window.theme.ProductItem(productItemsContainer);
        return;
      }
      
      // Try accessing directly (if not minified or exposed)
      if (typeof ProductItem === 'function') {
        ProductItem(productItemsContainer);
        return;
      }
      
      // Try accessing through theme sections (if available)
      if (window.theme && window.theme.sections && window.theme.sections.ProductItem) {
        window.theme.sections.ProductItem(productItemsContainer);
        return;
      }
      
      // Fallback: Try to trigger theme's section load event
      const event = new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { container: productItemsContainer }
      });
      productItemsContainer.dispatchEvent(event);
    }, 100);
  }
  
  /**
   * Clear style code results and restore no-results visibility
   */
  function clearStyleCodeResults() {
    const styleCodeContainer = document.querySelector('[data-style-code-container]');
    if (styleCodeContainer) {
      styleCodeContainer.innerHTML = '';
    }
    // Restore no-results visibility (let the theme handle showing/hiding based on actual results)
    const noResultsClass = document.querySelector('.quick-search__no-results');
    if (noResultsClass) {
      noResultsClass.style.display = '';
    }
  }

  /**
   * Format product price with custom discount support
   */
  function formatPrice(product) {
    if (!product.price) return '';
    
    const formatMoney = (cents) => {
      if (window.theme && window.theme.moneyFormat) {
        return window.theme.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => {
          if (key === 'amount') return (cents / 100).toFixed(2);
          if (key === 'amount_no_decimals') return Math.floor(cents / 100);
          if (key === 'amount_with_comma_separator') return (cents / 100).toFixed(2).replace('.', ',');
          return match;
        });
      }
      return '£' + (cents / 100).toFixed(2);
    };

    // Calculate adjusted prices if custom discount exists
    let adjustedPrice = product.price || 0;
    let adjustedCompareAtPrice = product.compare_at_price || 0;
    
    // Check for custom discount - handle both number and string types
    const discountPercentage = product.discount_percentage;
    const hasCustomDiscount = discountPercentage !== undefined && discountPercentage !== null && 
                              (typeof discountPercentage === 'number' ? discountPercentage > 0 : parseFloat(discountPercentage) > 0);
    
    // Debug logging
    if (discountPercentage !== undefined) {
      console.log('formatPrice: Product', product.title, 'discount_percentage:', discountPercentage, 'hasCustomDiscount:', hasCustomDiscount);
    }
    
    if (hasCustomDiscount) {
      const discountValue = typeof discountPercentage === 'number' ? discountPercentage : parseFloat(discountPercentage);
      const discountMultiplier = (100 - discountValue) / 100.0;
      
      const originalCompareAtPrice = product.compare_at_price || null;
      
      if (originalCompareAtPrice && originalCompareAtPrice > 0 && originalCompareAtPrice > product.price) {
        // If there's already a compare_at_price, apply discount to the sale price
        adjustedPrice = Math.round(product.price * discountMultiplier);
        adjustedCompareAtPrice = originalCompareAtPrice;
      } else {
        // If no compare_at_price, set original price as compare_at_price and apply discount
        adjustedCompareAtPrice = product.price;
        adjustedPrice = Math.round(product.price * discountMultiplier);
      }
    }

    // Calculate savings
    let savingsHtml = '';
    if (adjustedCompareAtPrice > adjustedPrice) {
      const savingsAmount = adjustedCompareAtPrice - adjustedPrice;
      savingsHtml = `<div class="product-item__savings"><span class="product-item__savings-text fs-body-small">Save ${formatMoney(savingsAmount)}</span></div>`;
    }

    // Format price display
    if (adjustedCompareAtPrice > adjustedPrice) {
      const result = `<span class="visually-hidden">Regular price</span><s class="t-subdued">${formatMoney(adjustedCompareAtPrice)}</s> <span class="sale">${formatMoney(adjustedPrice)}</span>${savingsHtml}`;
      console.log('formatPrice: Returning sale price HTML for', product.title, '- Compare:', adjustedCompareAtPrice, 'Price:', adjustedPrice);
      return result;
    }
    
    if (product.price_varies && !hasCustomDiscount) {
      return `From ${formatMoney(adjustedPrice)}`;
    }
    
    const result = formatMoney(adjustedPrice);
    console.log('formatPrice: Returning regular price for', product.title, '- Price:', adjustedPrice);
    return result;
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Get product IDs from predictive search results to avoid duplicates
   */
  function getExistingProductIds(resultsContainer) {
    const ids = new Set();
    const links = resultsContainer.querySelectorAll('a.quick-search__result[href*="/products/"]');
    links.forEach(link => {
      const href = link.getAttribute('href');
      if (href) {
        // Extract handle from URL
        const match = href.match(/\/products\/([^?#]+)/);
        if (match) {
          ids.add(match[1].toLowerCase());
        }
      }
    });
    return ids;
  }

  // Override fetch to intercept predictive search requests
  window.fetch = function(url, options) {
    // Check if this is a predictive search request
    if (typeof url === 'string' && url.includes('/search/suggest') && !url.includes('metafield-search')) {
      console.log('Custom Search: Intercepted predictive search request');
      // Parse the URL
      const urlObj = new URL(url, window.location.origin);
      const searchTerm = urlObj.searchParams.get('q');
      
      // Add product fields including variants.sku for SKU search
      if (!urlObj.searchParams.has('resources[options][fields]')) {
        urlObj.searchParams.set('resources[type]', 'product,collection,page,article,query');
        urlObj.searchParams.set('resources[limit]', '10');
        urlObj.searchParams.set('resources[options][fields]', 'title,product_type,variants.sku,variants.title,vendor,tag,body');
        urlObj.searchParams.set('resources[options][unavailable_products]', 'last');
      }
      
      // Make the modified predictive search request
      const predictivePromise = originalFetch.call(this, urlObj.toString(), options);
      
      // Also search by style code metafield (client-side)
      if (searchTerm && searchTerm.length >= 2) {
        console.log('Custom Search: Searching for style code:', searchTerm);
        
        // Ensure data is loaded (reload if needed)
        if (!allProductsData || allProductsData.length === 0) {
          console.log('Custom Search: Data not loaded, reloading...');
          getAllProductsData(true); // Force reload
        }
        
        // Search style codes immediately
        const styleCodeData = searchByStyleCode(searchTerm);
        console.log('Custom Search: Style code search returned', styleCodeData?.results?.length || 0, 'results');
        
        if (styleCodeData.results && styleCodeData.results.length > 0) {
          console.log('Custom Search: Found', styleCodeData.results.length, 'style code matches');
          
          // Render to dedicated container (won't be overwritten)
          renderStyleCodeResults(styleCodeData.results);
          
          // Hide no-results views since we have style code matches
          const noResultsView = document.querySelector('[data-quick-search-no-results]');
          const noResultsClass = document.querySelector('.quick-search__no-results');
          const defaultView = document.querySelector('[data-quick-search-default]');
          if (noResultsView) noResultsView.style.display = 'none';
          if (noResultsClass) noResultsClass.style.display = 'none';
          if (defaultView) defaultView.style.display = 'none';
        } else {
          // No style code matches, clear the container and restore no-results visibility
          clearStyleCodeResults();
          const noResultsClass = document.querySelector('.quick-search__no-results');
          if (noResultsClass) noResultsClass.style.display = '';
        }
      } else {
        // Search term too short, clear style code results
        clearStyleCodeResults();
      }
      
      return predictivePromise;
    }
    
    // For all other requests, use original fetch
    return originalFetch.apply(this, arguments);
  };

  // Eagerly load the products data when DOM is ready
  function initializeSearch() {
    // Reset cache to force reload
    allProductsData = null;
    const data = getAllProductsData();
    console.log('Custom Search: Initialization - Products loaded:', data.length);
    setupClearHandler();
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      // Wait a bit for scripts to be parsed
      setTimeout(initializeSearch, 200);
    });
  } else {
    // If DOM is already ready, wait a bit for scripts to load
    setTimeout(initializeSearch, 200);
  }
  
  // Also reload data when quick search input is focused (in case script loads late)
  document.addEventListener('focusin', function(e) {
    if (e.target.matches && e.target.matches('.quick-search__input, [data-input]')) {
      setTimeout(function() {
        if (!allProductsData || allProductsData.length === 0) {
          console.log('Custom Search: Quick search focused, reloading data...');
          allProductsData = null; // Reset cache
          getAllProductsData();
        }
      }, 100);
    }
  }, true);
  
  // Clear style code results when search is cleared
  function setupClearHandler() {
    // Listen for clear button clicks
    const clearButton = document.querySelector('[data-quick-search] [data-clear]');
    if (clearButton) {
      clearButton.addEventListener('click', function() {
        clearStyleCodeResults();
      });
    }
    
    // Listen for input changes (to clear when empty)
    const searchInput = document.querySelector('[data-quick-search] [data-input]');
    if (searchInput) {
      searchInput.addEventListener('input', function() {
        if (this.value.length < 2) {
          clearStyleCodeResults();
        }
      });
    }
  }

  console.log('Custom Search: SKU and Style Code search enabled');

  // Fix iOS scrolling issue: Patch disableBodyScroll and handleScroll to allow touch scrolling on results wrapper
  (function() {
    // Store original disableBodyScroll if it exists
    if (typeof window.disableBodyScroll === 'function') {
      const originalDisableBodyScroll = window.disableBodyScroll;
      
      // Patch disableBodyScroll to always allow touch moves on elements with data-scroll-lock-ignore
      window.disableBodyScroll = function(targetElement, options) {
        // If options don't have allowTouchMove, add it
        if (!options) {
          options = {};
        }
        
        if (!options.allowTouchMove) {
          options.allowTouchMove = function(el) {
            // Check if element or any parent has data-scroll-lock-ignore
            let currentEl = el;
            while (currentEl && currentEl !== document.body) {
              if (currentEl.getAttribute && currentEl.getAttribute('data-scroll-lock-ignore') !== null) {
                return true;
              }
              // Also check if it's the results wrapper or inside it
              if (currentEl.classList && currentEl.classList.contains('quick-search__results-wrapper')) {
                return true;
              }
              // Check if it's any child of results wrapper
              const resultsWrapper = document.querySelector('.quick-search__results-wrapper');
              if (resultsWrapper && resultsWrapper.contains(currentEl)) {
                return true;
              }
              currentEl = currentEl.parentNode;
            }
            return false;
          };
        } else {
          // If allowTouchMove already exists, enhance it
          const originalAllowTouchMove = options.allowTouchMove;
          options.allowTouchMove = function(el) {
            // First check original function
            if (originalAllowTouchMove(el)) {
              return true;
            }
            // Then check our conditions
            let currentEl = el;
            while (currentEl && currentEl !== document.body) {
              if (currentEl.classList && currentEl.classList.contains('quick-search__results-wrapper')) {
                return true;
              }
              const resultsWrapper = document.querySelector('.quick-search__results-wrapper');
              if (resultsWrapper && resultsWrapper.contains(currentEl)) {
                return true;
              }
              currentEl = currentEl.parentNode;
            }
            return false;
          };
        }
        
        return originalDisableBodyScroll(targetElement, options);
      };
    }

    // Patch the handleScroll logic to allow scrolling on results wrapper
    // This fixes the issue where scrolling gets locked at the bottom
    function patchScrollHandlers() {
      const resultsWrapper = document.querySelector('.quick-search__results-wrapper');
      if (!resultsWrapper) return;

      // Prevent body scroll lock from interfering with results wrapper scrolling
      // By stopping propagation at capture phase, we prevent the body scroll lock handlers from running
      // This allows the results wrapper to scroll freely in both directions
      resultsWrapper.addEventListener('touchmove', function(e) {
        // Stop propagation to prevent body scroll lock from interfering
        // Don't preventDefault() - we want native scrolling to work
        // This allows scrolling in both directions, even at boundaries
        e.stopImmediatePropagation();
      }, { passive: false, capture: true }); // passive: false to allow stopImmediatePropagation, but we don't call preventDefault

      // Also ensure the element itself can scroll
      resultsWrapper.style.webkitOverflowScrolling = 'touch';
      resultsWrapper.style.touchAction = 'pan-y';
    }

    // Ensure results wrapper has proper iOS scrolling properties
    function initScrollFix() {
      const resultsWrapper = document.querySelector('.quick-search__results-wrapper');
      if (resultsWrapper) {
        resultsWrapper.setAttribute('data-scroll-lock-ignore', '');
        patchScrollHandlers();
      }
    }

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initScrollFix);
    } else {
      initScrollFix();
    }

    // Also try after a delay in case the element is added dynamically
    setTimeout(initScrollFix, 500);
  })();
})();

